'use client';

import { useCallback, useEffect, useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { MfaMessagesToggleHeader } from '../../mfa/MfaMessagesToggleHeader';
import { EmailMessageBody } from '../../mfa/EmailMessageBody';
import { EmailRawSourceViewer } from '../../mfa/EmailRawSourceViewer';
import { BodyStrong, MetaDefault } from '../../../components/typography';
import { Spinner } from '../../../components/spinner';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import type {
  CredentialsApiDto,
  MfaMessageDto,
} from '@terra/shared-contracts-applications';
import { OtpDisplay } from '../../mfa/OtpDisplay';
import { useLiveOtpCode } from '../../mfa/use-live-otp-code';
import { ReadOnlyField } from './ReadOnlyField';
import {
  useCredentialDataSources,
  type MfaWebhookConfigDetails,
} from '../CredentialDataSourcesContext';

type Credential = CredentialsApiDto['credentials'][number];
type OtpMfaConfig = NonNullable<Credential['mfaConfig']>[number];

const MfaSectionHeader = ({ title }: { title: string }) => (
  <BodyStrong color="fg.neutral.default">{title}</BodyStrong>
);

type MfaMessagesRefreshNamespace =
  | 'designSystem.mfa.smsDisplay'
  | 'designSystem.mfa.webhookDisplay';

type MfaMessagesSectionProps = {
  credentialId: string;
  mfaConfigId: string;
  buttonLabel: string;
  refreshI18nNamespace: MfaMessagesRefreshNamespace;
  variant?: 'email' | 'plain';
};

const MfaMessagesSection = ({
  credentialId,
  mfaConfigId,
  buttonLabel,
  refreshI18nNamespace,
  variant = 'plain',
}: MfaMessagesSectionProps) => {
  const tCard = useTranslations('designSystem.credentials.card');
  const tRefresh = useTranslations(refreshI18nNamespace);
  const dateFormatter = useDateFormatter();
  const { fetchMfaMessages } = useCredentialDataSources();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<MfaMessageDto[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadMessages = useCallback(async () => {
    if (!fetchMfaMessages) {
      return;
    }

    setIsLoadingMessages(true);

    try {
      const result = await fetchMfaMessages({ credentialId, mfaConfigId });

      if (result.success) {
        setMessages(result.messages ?? []);
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch (error) {
      console.error('Failed to fetch MFA messages:', error);
      setLoadFailed(true);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [fetchMfaMessages, credentialId, mfaConfigId]);

  const handleToggle = useCallback(async () => {
    if (isOpen) {
      setIsOpen(false);

      return;
    }

    setIsOpen(true);
    await loadMessages();
  }, [isOpen, loadMessages]);

  const isInitialLoad = isLoadingMessages && messages.length === 0;

  return (
    <VStack alignItems="stretch" gap={2}>
      <MfaMessagesToggleHeader
        title={buttonLabel}
        isExpanded={isOpen}
        onToggle={handleToggle}
        toggleLoading={isLoadingMessages && !isOpen}
        toggleAriaLabel={
          isOpen ? tRefresh('hideMessages') : tRefresh('showMessages')
        }
        refreshLabel={tRefresh('refresh')}
        onRefresh={loadMessages}
        refreshLoading={isLoadingMessages}
      />
      {isOpen && isInitialLoad ? (
        <MetaDefault color="fg.neutral.muted">
          {tRefresh('loadingMessages')}
        </MetaDefault>
      ) : null}
      {isOpen && !isInitialLoad ? (
        <VStack alignItems="stretch" gap={2}>
          {loadFailed ? (
            <MetaDefault color="fg.neutral.error">
              {tRefresh('failedToLoadMessages')}
            </MetaDefault>
          ) : null}
          {messages.length === 0 && !loadFailed ? (
            <MetaDefault color="fg.neutral.muted">
              {tCard('tabs.mfa.noMessages')}
            </MetaDefault>
          ) : (
            messages.map((msg) => (
              <Box
                key={msg.id}
                px={3}
                py={2}
                borderWidth="1px"
                borderColor="border.neutral.subtle"
                borderRadius="sm"
                bg="bg.neutral.subtle"
              >
                <VStack alignItems="stretch" gap={2}>
                  {variant === 'email' ? (
                    <>
                      <EmailMessageBody
                        content={msg.content}
                        links={msg.links}
                      />
                      <EmailRawSourceViewer
                        rawPlain={msg.rawPlain}
                        rawHtml={msg.rawHtml}
                      />
                    </>
                  ) : (
                    <MetaDefault color="fg.neutral.default">
                      {msg.content}
                    </MetaDefault>
                  )}
                  <MetaDefault color="fg.neutral.subtle">
                    {dateFormatter.format(new Date(msg.receivedAt))}
                    {msg.from ? ` — ${msg.from}` : ''}
                  </MetaDefault>
                </VStack>
              </Box>
            ))
          )}
        </VStack>
      ) : null}
    </VStack>
  );
};

interface WebhookDetailsProps {
  credentialId: string;
  mfaConfigId: string;
}
const WebhookDetails = ({ credentialId, mfaConfigId }: WebhookDetailsProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const [config, setConfig] = useState<MfaWebhookConfigDetails | null>(null);
  const { fetchMfaWebhookConfig } = useCredentialDataSources();
  const [isLoading, setIsLoading] = useState(Boolean(fetchMfaWebhookConfig));

  useEffect(() => {
    if (!fetchMfaWebhookConfig) {
      return;
    }

    const fetchConfig = async () => {
      try {
        const result = await fetchMfaWebhookConfig({
          credentialId,
          mfaConfigId,
        });

        if (result.success) {
          const { success: _success, ...details } = result;

          setConfig(details);
        }
      } catch (error) {
        console.error('Failed to fetch webhook config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchConfig();
  }, [credentialId, fetchMfaWebhookConfig, mfaConfigId]);
  if (isLoading) {
    return <Spinner size="sm" />;
  }

  if (!config) {
    return (
      <MetaDefault color="fg.neutral.muted">
        {t('tabs.mfa.webhookConfigUnavailable')}
      </MetaDefault>
    );
  }

  const headersText = [
    `${config.timestampHeader}: <unix_seconds>`,
    `${config.signatureHeader}: <base64_hmac_sha256(timestamp.rawBody, secret)>`,
    `${config.deliveryIdHeader}: <optional_uuid>`,
  ].join('\n');

  return (
    <VStack alignItems="stretch" gap={3}>
      <ReadOnlyField
        label={t('tabs.mfa.webhookEndpoint')}
        value={config.endpointPath}
        helperText={t('tabs.mfa.webhookEndpointHelper')}
      />
      <ReadOnlyField
        label={t('tabs.mfa.requiredHeaders')}
        value={headersText}
        multiline
        copyable={false}
      />
      <ReadOnlyField
        label={t('tabs.mfa.signingSecret')}
        value={config.secret}
        helperText={t('tabs.mfa.signingSecretHelper')}
      />
    </VStack>
  );
};

type CredentialOtpSectionProps = {
  credentialId: string;
  otpConfig: OtpMfaConfig;
};

const CredentialOtpSection = ({
  credentialId,
  otpConfig,
}: CredentialOtpSectionProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const { fetchMfaMessages } = useCredentialDataSources();
  const { otpCode, isOtpLoading, fetchOtpCode } = useLiveOtpCode({
    credentialId,
    mfaConfigId: otpConfig.id,
    fetchMfaMessages,
    initialCode: otpConfig.lastOtpCode ?? '',
    enabled: Boolean(fetchMfaMessages),
  });

  if (!otpCode && !isOtpLoading) {
    return (
      <MetaDefault color="fg.neutral.subtle">
        {t('tabs.mfa.otpConfigured')}
      </MetaDefault>
    );
  }

  return (
    <OtpDisplay
      code={otpCode}
      isLoading={isOtpLoading}
      onRefresh={fetchMfaMessages ? fetchOtpCode : undefined}
    />
  );
};

type CredentialMfaSectionProps = {
  credential: Credential;
};

export const CredentialMfaSection = ({
  credential,
}: CredentialMfaSectionProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const activeMfaConfigs =
    credential.mfaConfig?.filter(({ isActive }) => isActive) ?? [];

  const hasDetailProviders = activeMfaConfigs.some(
    ({ providerType }) => providerType !== 'EMAIL',
  );

  if (!hasDetailProviders) {
    return null;
  }

  const smsConfig = activeMfaConfigs.find(
    ({ providerType }) => providerType === 'SMS',
  );
  const otpConfig = activeMfaConfigs.find(
    ({ providerType }) => providerType === 'OTP',
  );
  const webhookConfig = activeMfaConfigs.find(
    ({ providerType }) => providerType === 'WEBHOOK',
  );
  const sections = [
    smsConfig && (
      <VStack key="sms" alignItems="stretch" gap={3}>
        <MfaSectionHeader title={t('tabs.mfa.sms')} />
        {smsConfig.phoneNumber && (
          <ReadOnlyField
            label={t('tabs.mfa.generatedPhoneNumber')}
            value={smsConfig.phoneNumber}
            helperText={t('tabs.mfa.smsHelper')}
          />
        )}
        <MfaMessagesSection
          credentialId={credential.id}
          mfaConfigId={smsConfig.id}
          buttonLabel={t('tabs.mfa.viewLatestSms')}
          refreshI18nNamespace="designSystem.mfa.smsDisplay"
        />
      </VStack>
    ),
    otpConfig && (
      <VStack key="otp" alignItems="stretch" gap={3}>
        <MfaSectionHeader title={t('tabs.mfa.otp')} />
        <CredentialOtpSection
          credentialId={credential.id}
          otpConfig={otpConfig}
        />
      </VStack>
    ),
    webhookConfig && (
      <VStack key="webhook" alignItems="stretch" gap={3}>
        <MfaSectionHeader title={t('tabs.mfa.webhook')} />
        <WebhookDetails
          credentialId={credential.id}
          mfaConfigId={webhookConfig.id}
        />
        <MfaMessagesSection
          credentialId={credential.id}
          mfaConfigId={webhookConfig.id}
          buttonLabel={t('tabs.mfa.viewLatestMessages')}
          refreshI18nNamespace="designSystem.mfa.webhookDisplay"
        />
      </VStack>
    ),
  ].filter(Boolean);

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      {sections.map((section, index) => (
        <Box key={index}>
          {index > 0 && (
            <Box
              borderBottomWidth="1px"
              borderColor="border.neutral.subtle"
              mb={3}
            />
          )}
          {section}
        </Box>
      ))}
    </VStack>
  );
};
